/**
 * Service for handling file operations including fetching content from URLs
 */

export interface FileFetchResult {
  success: boolean;
  content?: string;
  error?: string;
  contentType?: string;
  status?: number; // HTTP status code for error handling
}

/**
 * Fetches file content from a URL
 * @param url - The URL to fetch content from
 * @returns Promise with file content or error
 */
export async function fetchFileContent(url: string): Promise<FileFetchResult> {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      return {
        success: false,
        error: `Failed to fetch file: ${response.status} ${response.statusText}`
      };
    }

    const contentType = response.headers.get('content-type') || '';
    const lowerUrl = url.toLowerCase();
    const mimeBase = contentType.split(';')[0].trim().toLowerCase();

    // For PDFs, we can pass the URL directly to the PDF viewer
    if (mimeBase.includes('application/pdf') || lowerUrl.endsWith('.pdf')) {
      return {
        success: true,
        content: url, // Pass URL directly for PDFs
        contentType
      };
    }

    // Word documents are binary; response.text() corrupts them
    if (
      mimeBase === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      lowerUrl.endsWith('.docx')
    ) {
      const buf = await response.arrayBuffer();
      const blob = new Blob([buf], {
        type:
          mimeBase ||
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      return {
        success: true,
        content: URL.createObjectURL(blob),
        contentType: blob.type,
      };
    }
    if (mimeBase === 'application/msword' || lowerUrl.endsWith('.doc')) {
      const buf = await response.arrayBuffer();
      const blob = new Blob([buf], { type: mimeBase || 'application/msword' });
      return {
        success: true,
        content: URL.createObjectURL(blob),
        contentType: blob.type,
      };
    }
    if (
      mimeBase ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      lowerUrl.endsWith('.xlsx')
    ) {
      const buf = await response.arrayBuffer();
      const blob = new Blob([buf], {
        type:
          mimeBase ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      return {
        success: true,
        content: URL.createObjectURL(blob),
        contentType: blob.type,
      };
    }
    if (mimeBase === 'application/vnd.ms-excel' || lowerUrl.endsWith('.xls')) {
      const buf = await response.arrayBuffer();
      const blob = new Blob([buf], { type: mimeBase || 'application/vnd.ms-excel' });
      return {
        success: true,
        content: URL.createObjectURL(blob),
        contentType: blob.type,
      };
    }

    // For other file types, fetch the content
    const content = await response.text();
    
    return {
      success: true,
      content,
      contentType
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Determines if a URL points to a PDF file
 * @param url - The URL to check
 * @returns boolean indicating if it's a PDF
 */
export function isPdfUrl(url: string): boolean {
  return url.toLowerCase().endsWith('.pdf') || url.includes('application/pdf');
}

/** Extensions for files that must not use Blob.text() (binary formats). */
const BINARY_FILE_EXT_RE =
  /\.(pdf|docx|doc|png|jpe?g|gif|webp|bmp|tiff?|ico|heic|heif|xlsx?|xls|pptx?|ppt|zip|jar|mp4|mp3|wav|wasm)$/i;

function fileNameSuggestsBinaryBlob(fileNameHint?: string): boolean {
  const n = (fileNameHint || '').trim();
  if (!n) return false;
  return BINARY_FILE_EXT_RE.test(n);
}

/**
 * When Content-Type clearly indicates text, prefer `Blob.text()` even if the filename
 * looks binary (misnamed uploads: e.g. `.xls` / `.zip` / `.json` saved under a wrong ext).
 */
function mimeLooksClearlyText(mime: string): boolean {
  const base = (mime || '').split(';')[0].trim().toLowerCase();
  if (!base) return false;
  if (base.startsWith('text/')) return true;
  if (
    base === 'application/json' ||
    base === 'application/xml' ||
    base === 'application/csv' ||
    base === 'application/xhtml+xml' ||
    base === 'application/sql' ||
    base === 'application/yaml' ||
    base === 'application/x-yaml' ||
    base === 'application/x-ndjson' ||
    base === 'application/graphql-response+json'
  ) {
    return true;
  }
  if (base.endsWith('+json') || base.endsWith('+xml')) return true;
  return false;
}

function shouldUseObjectUrlForBlob(mime: string, fileNameHint?: string): boolean {
  const m = (mime || '').toLowerCase();
  if (m.includes('application/pdf') || m.startsWith('image/')) return true;
  if (
    m.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
    m.includes('application/msword')
  ) {
    return true;
  }
  if (
    m.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
    m.includes('application/vnd.ms-excel')
  ) {
    return true;
  }
  if (mimeLooksClearlyText(mime)) return false;
  // APIs often return Word/PDF as application/octet-stream; text() corrupts bytes and breaks preview fetch().
  if (m === 'application/octet-stream' && fileNameSuggestsBinaryBlob(fileNameHint)) return true;
  if (fileNameSuggestsBinaryBlob(fileNameHint)) return true;
  return false;
}

/**
 * Fetches file content using the new download API with file ID
 * @param fileId - The file ID to fetch
 * @param inline - Whether to fetch for inline viewing (default: true)
 * @param parsed - Whether to fetch parsed/processed content instead of raw file (default: false)
 * @param fileNameHint - Original file name; used to choose binary-safe handling when MIME is wrong (e.g. octet-stream)
 * @returns Promise with file content or error
 */
export async function fetchFileContentById(
  fileId: string,
  inline: boolean = true,
  parsed: boolean = false,
  fileNameHint?: string,
): Promise<FileFetchResult> {
  try {
    const { apiService } = await import('./apiService');
    const response = await apiService.downloadFile(fileId, inline, parsed);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to fetch file content',
        status: response.status // Include status code for 404 detection (404 = no parsed content available)
      };
    }

    // PDFs, images, Word, etc. need binary-safe handling; text() corrupts binary data
    if (shouldUseObjectUrlForBlob(response.data.type, fileNameHint)) {
      const url = URL.createObjectURL(response.data);
      return {
        success: true,
        content: url,
        contentType: response.data.type
      };
    } else {
      const content = await response.data.text();
      return {
        success: true,
        content,
        contentType: response.data.type
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function fetchDocumentById(documentId: string, fileNameHint?: string): Promise<FileFetchResult> {
  try {
    const { apiService } = await import('./apiService');
    const response = await apiService.downloadDocumentById(documentId);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to fetch document'
      };
    }

    if (shouldUseObjectUrlForBlob(response.data.type, fileNameHint)) {
      const url = URL.createObjectURL(response.data);
      return {
        success: true,
        content: url,
        contentType: response.data.type
      };
    } else {
      const content = await response.data.text();
      return {
        success: true,
        content,
        contentType: response.data.type
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Determines if a URL points to a text-based file that should be fetched
 * @param url - The URL to check
 * @returns boolean indicating if content should be fetched
 */
export function shouldFetchContent(url: string): boolean {
  const textExtensions = [
    '.txt',
    '.html',
    '.htm',
    '.json',
    '.xml',
    '.csv',
    '.edi',
    '.dat',
    '.x12',
    '.hl7',
    '.fhir',
  ];
  const lowerUrl = url.toLowerCase();
  
  // Check standard text extensions
  if (textExtensions.some(ext => lowerUrl.endsWith(ext))) return true;
  
  return (
    lowerUrl.includes('text/') ||
    lowerUrl.includes('application/json') ||
    lowerUrl.includes('application/xml') ||
    lowerUrl.includes('text/html')
  );
}
